import type {
  EntryMode,
  KpiGridColumn,
  KpiGridFixedRow,
  KpiOperand,
  KpiOperation,
  KpiOption,
  RollupMode,
} from "../types/kpiEntryMode";
import {
  ENTRY_MODE_CALCULATED,
  ENTRY_MODE_MANUAL,
  isGridDataTypeName,
  isGridHeadingDataTypeName,
  isMeasuredQuantityDataTypeName,
  isMultiselectDataTypeName,
  isSelectDataTypeName,
} from "../types/kpiEntryMode";
import type { Kpi } from "../types/kpi";
import type { KpiDataType } from "../types/kpiDataTypes";
import type { CreateKpiRequest, UpdateKpiRequest } from "../types/kpiRequests";

export type KpiFormTab = "basics" | "formula" | "options" | "grid";

export type KpiFormState = {
  name: string;
  description: string;
  data_type_id: number | "";
  unit_id: number | "";
  entry_mode: EntryMode | string;
  operation: KpiOperation | string;
  decimal_places: number | "";
  rollup_mode: RollupMode | string;
  operands: KpiOperand[];
  options: KpiOption[];
  min_selections: number | "";
  max_selections: number | "";
  /** Predefined list code for SELECT/MULTISELECT; "" = typed options. */
  predefined_option_list_code: string;
  grid_min_rows: number | "";
  grid_max_rows: number | "";
  grid_show_column_totals: boolean;
  grid_show_row_totals: boolean;
  grid_columns: KpiGridColumn[];
  grid_fixed_rows: KpiGridFixedRow[];
};

export const EMPTY_KPI_FORM: KpiFormState = {
  name: "",
  description: "",
  data_type_id: "",
  unit_id: "",
  entry_mode: ENTRY_MODE_MANUAL,
  operation: "",
  decimal_places: "",
  rollup_mode: "SUM",
  operands: [],
  options: [],
  min_selections: "",
  max_selections: "",
  predefined_option_list_code: "",
  grid_min_rows: "",
  grid_max_rows: "",
  grid_show_column_totals: false,
  grid_show_row_totals: false,
  grid_columns: [],
  grid_fixed_rows: [],
};

export function isManualOnlyDataTypeName(name?: string | null): boolean {
  return (
    isGridDataTypeName(name) ||
    isSelectDataTypeName(name) ||
    isMultiselectDataTypeName(name)
  );
}

export function isRollupLockedNoneDataTypeName(name?: string | null): boolean {
  return isSelectDataTypeName(name) || isMultiselectDataTypeName(name);
}

export function formFromKpi(kpi: Kpi): KpiFormState {
  const dataTypeName = (kpi.data_type_name || "").toUpperCase();
  const entryMode = isManualOnlyDataTypeName(dataTypeName)
    ? ENTRY_MODE_MANUAL
    : (kpi.entry_mode as string) || ENTRY_MODE_MANUAL;
  const rollupMode = isRollupLockedNoneDataTypeName(dataTypeName)
    ? "NONE"
    : (kpi.rollup_mode as string) || "SUM";

  return {
    name: kpi.kpi_name ?? "",
    description: kpi.description ?? "",
    data_type_id: kpi.data_type_id ?? "",
    unit_id: shouldHideUnit(dataTypeName) ? "" : (kpi.unit_id ?? ""),
    entry_mode: entryMode,
    operation: (kpi.operation as string) || "",
    decimal_places: kpi.decimal_places ?? "",
    rollup_mode: rollupMode,
    operands: [...(kpi.operands ?? [])].sort(
      (a, b) => (a.list_order ?? 0) - (b.list_order ?? 0)
    ),
    options: sortOptionsOtherLast([...(kpi.options ?? [])]),
    min_selections: kpi.min_selections ?? "",
    max_selections: kpi.max_selections ?? "",
    predefined_option_list_code: kpi.predefined_option_list_code ?? "",
    grid_min_rows: kpi.grid_min_rows ?? "",
    grid_max_rows: kpi.grid_max_rows ?? "",
    grid_show_column_totals: !!kpi.grid_show_column_totals,
    grid_show_row_totals: !!kpi.grid_show_row_totals,
    grid_columns: [...(kpi.grid_columns ?? [])]
      .sort((a, b) => (a.list_order ?? 0) - (b.list_order ?? 0))
      .map((col) => ({
        ...col,
        options: sortOptionsOtherLast([...(col.options ?? [])]),
      })),
    grid_fixed_rows: [...(kpi.grid_fixed_rows ?? [])].sort(
      (a, b) => (a.list_order ?? 0) - (b.list_order ?? 0)
    ),
  };
}

export function resolveDataTypeName(
  dataTypeId: number | "",
  dataTypes: KpiDataType[]
): string {
  if (!dataTypeId) return "";
  return (
    dataTypes.find((dt) => dt.data_type_id === Number(dataTypeId))
      ?.data_type_name ?? ""
  );
}

export function visibleKpiFormTabs(
  form: KpiFormState,
  dataTypeName: string
): KpiFormTab[] {
  const tabs: KpiFormTab[] = ["basics"];
  const entryMode = isManualOnlyDataTypeName(dataTypeName)
    ? ENTRY_MODE_MANUAL
    : (form.entry_mode || "").toUpperCase();
  if (entryMode === ENTRY_MODE_CALCULATED) {
    tabs.push("formula");
  }
  if (isSelectDataTypeName(dataTypeName) || isMultiselectDataTypeName(dataTypeName)) {
    tabs.push("options");
  }
  if (isGridDataTypeName(dataTypeName)) {
    tabs.push("grid");
  }
  return tabs;
}

export function shouldHideUnit(dataTypeName: string): boolean {
  return !isMeasuredQuantityDataTypeName(dataTypeName);
}

/** Patch applied when the admin changes data type on Basics. */
export function dataTypeChangePatch(dataTypeName: string): Partial<KpiFormState> {
  const patch: Partial<KpiFormState> = {};
  if (shouldHideUnit(dataTypeName)) {
    patch.unit_id = "";
  }
  if (isManualOnlyDataTypeName(dataTypeName)) {
    patch.entry_mode = ENTRY_MODE_MANUAL;
    patch.operation = "";
    patch.operands = [];
    patch.decimal_places = "";
  }
  if (isRollupLockedNoneDataTypeName(dataTypeName)) {
    patch.rollup_mode = "NONE";
  }
  if (isSelectDataTypeName(dataTypeName)) {
    patch.max_selections = 1;
  }
  if (!isSelectDataTypeName(dataTypeName) && !isMultiselectDataTypeName(dataTypeName)) {
    patch.predefined_option_list_code = "";
  }
  return patch;
}

function numOrNull(value: number | ""): number | null {
  return value === "" || value === null || value === undefined
    ? null
    : Number(value);
}

function sortOptionsOtherLast<T extends { allows_input?: boolean; list_order?: number }>(
  options: T[]
): T[] {
  const normal = options
    .filter((o) => !o.allows_input)
    .sort((a, b) => (a.list_order ?? 0) - (b.list_order ?? 0));
  const other = options.filter((o) => !!o.allows_input);
  return [...normal, ...other];
}

function mapOptionsForPayload(
  options: Array<{
    kpi_option_id?: number;
    kpi_grid_column_option_id?: number;
    label: string;
    allows_input?: boolean;
    is_active?: boolean;
  }>
) {
  return sortOptionsOtherLast(options).map((opt, idx) => {
    const base: {
      kpi_option_id?: number;
      kpi_grid_column_option_id?: number;
      label: string;
      list_order: number;
      allows_input: boolean;
      is_active: boolean;
    } = {
      label: opt.label,
      list_order: idx,
      allows_input: !!opt.allows_input,
      is_active: opt.is_active !== false,
    };
    if (opt.kpi_option_id) {
      base.kpi_option_id = opt.kpi_option_id;
    }
    if (opt.kpi_grid_column_option_id) {
      base.kpi_grid_column_option_id = opt.kpi_grid_column_option_id;
    }
    return base;
  });
}

function mapFixedRowsForPayload(rows: KpiGridFixedRow[]): KpiGridFixedRow[] {
  return rows.map((row, idx) => {
    const base: KpiGridFixedRow = {
      label: row.label,
      list_order: idx,
      is_active: row.is_active !== false,
    };
    if (row.kpi_grid_fixed_row_id) {
      base.kpi_grid_fixed_row_id = row.kpi_grid_fixed_row_id;
    }
    return base;
  });
}

export function buildKpiPayload(
  form: KpiFormState,
  dataTypeName: string
): CreateKpiRequest & UpdateKpiRequest {
  const manualOnly = isManualOnlyDataTypeName(dataTypeName);
  const entryMode = manualOnly
    ? ENTRY_MODE_MANUAL
    : (form.entry_mode || ENTRY_MODE_MANUAL).toUpperCase();
  const isCalculated = entryMode === ENTRY_MODE_CALCULATED;
  const isSelect = isSelectDataTypeName(dataTypeName);
  const isMulti = isMultiselectDataTypeName(dataTypeName);
  const isGrid = isGridDataTypeName(dataTypeName);
  const rollupMode = isRollupLockedNoneDataTypeName(dataTypeName)
    ? "NONE"
    : (form.rollup_mode || "SUM").toUpperCase();

  const payload: CreateKpiRequest & UpdateKpiRequest = {
    name: form.name.trim(),
    description: form.description?.trim() || undefined,
    data_type_id: form.data_type_id ? Number(form.data_type_id) : undefined,
    unit_id: shouldHideUnit(dataTypeName)
      ? undefined
      : form.unit_id
        ? Number(form.unit_id)
        : undefined,
    entry_mode: entryMode,
    rollup_mode: rollupMode,
  };

  if (isCalculated) {
    payload.operation = form.operation || null;
    payload.decimal_places = numOrNull(form.decimal_places);
    payload.operands = form.operands.map((op, idx) => ({
      source_kpi_id: op.source_kpi_id,
      source_activity_id: op.source_activity_id,
      list_order: idx,
    }));
  } else {
    payload.operation = null;
    payload.operands = [];
    payload.decimal_places = null;
  }

  if (isSelect || isMulti) {
    const listCode = form.predefined_option_list_code || null;
    payload.predefined_option_list_code = listCode;
    payload.options = listCode ? [] : mapOptionsForPayload(form.options);
    payload.min_selections = numOrNull(form.min_selections);
    payload.max_selections = isSelect ? 1 : numOrNull(form.max_selections);
  } else {
    payload.predefined_option_list_code = null;
    payload.options = [];
    payload.min_selections = null;
    payload.max_selections = null;
  }

  if (isGrid) {
    payload.grid_min_rows = numOrNull(form.grid_min_rows);
    payload.grid_max_rows = numOrNull(form.grid_max_rows);
    payload.grid_show_column_totals = !!form.grid_show_column_totals;
    payload.grid_show_row_totals = !!form.grid_show_row_totals;
    payload.grid_columns = form.grid_columns.map((col, idx) => ({
      ...(col.kpi_grid_column_id
        ? { kpi_grid_column_id: col.kpi_grid_column_id }
        : {}),
      label: col.label,
      data_type_id: Number(col.data_type_id),
      list_order: idx,
      include_in_rollup: !!col.include_in_rollup,
      is_active: col.is_active !== false,
      min_selections: col.min_selections ?? undefined,
      max_selections: col.max_selections ?? undefined,
      predefined_option_list_code: col.predefined_option_list_code || null,
      options: col.predefined_option_list_code
        ? []
        : mapOptionsForPayload(col.options ?? []),
    }));
    payload.grid_fixed_rows = mapFixedRowsForPayload(form.grid_fixed_rows);
  } else {
    payload.grid_columns = [];
    payload.grid_fixed_rows = [];
    payload.grid_min_rows = null;
    payload.grid_max_rows = null;
    payload.grid_show_column_totals = false;
    payload.grid_show_row_totals = false;
  }

  return payload;
}

function assertOtherLast(
  options: Array<{ allows_input?: boolean; label: string }>,
  messages: string[],
  context: string
): void {
  const otherIndexes = options
    .map((o, i) => (o.allows_input ? i : -1))
    .filter((i) => i >= 0);
  if (otherIndexes.length > 1) {
    messages.push(`Only one ${context} may allow input ("Other").`);
  } else if (
    otherIndexes.length === 1 &&
    otherIndexes[0] !== options.length - 1
  ) {
    messages.push(`The "Other" ${context} must be last.`);
  }
}

export function validateKpiForm(
  form: KpiFormState,
  dataTypeName: string,
  dataTypes: KpiDataType[] = []
): string[] {
  const messages: string[] = [];
  if (!form.name?.trim()) messages.push("KPI name is required.");
  if (!form.data_type_id) messages.push("Data type is required.");
  if (!shouldHideUnit(dataTypeName) && !form.unit_id) {
    messages.push("Unit is required.");
  }

  const entryMode = isManualOnlyDataTypeName(dataTypeName)
    ? ENTRY_MODE_MANUAL
    : (form.entry_mode || ENTRY_MODE_MANUAL).toUpperCase();
  if (
    isManualOnlyDataTypeName(dataTypeName) &&
    (form.entry_mode || "").toUpperCase() === ENTRY_MODE_CALCULATED
  ) {
    messages.push(
      "Grid, Select, and Multiselect KPIs must use Manual entry mode."
    );
  }
  if (entryMode === ENTRY_MODE_CALCULATED) {
    if (!form.operation) messages.push("Operation is required for calculated KPIs.");
    if (form.operands.length === 0) {
      messages.push("At least one operand is required for calculated KPIs.");
    }
    if (form.operands.some((op) => !op.source_kpi_id || !op.source_activity_id)) {
      messages.push("Each operand needs a KPI and an activity.");
    }
  }

  if (isSelectDataTypeName(dataTypeName) || isMultiselectDataTypeName(dataTypeName)) {
    if (!form.predefined_option_list_code) {
      if (form.options.length === 0) messages.push("At least one option is required.");
      if (form.options.some((o) => !o.label.trim())) {
        messages.push("Every option needs a label.");
      }
      assertOtherLast(form.options, messages, "option");
    }
  }

  if (isGridDataTypeName(dataTypeName)) {
    if (form.grid_columns.length === 0) {
      messages.push("At least one grid column is required.");
    }
    if (form.grid_columns.some((c) => !c.label.trim() || !c.data_type_id)) {
      messages.push("Every grid column needs a label and data type.");
    }
    for (const col of form.grid_columns) {
      if (col.predefined_option_list_code) continue;
      assertOtherLast(col.options ?? [], messages, "grid column option");
    }
    if (form.grid_fixed_rows.some((r) => !r.label.trim())) {
      messages.push("Every fixed row needs a label.");
    }
    const hasActiveFixed = form.grid_fixed_rows.some((r) => r.is_active !== false);
    if (hasActiveFixed && form.grid_columns.length > 0) {
      const firstCol = [...form.grid_columns].sort(
        (a, b) => (a.list_order ?? 0) - (b.list_order ?? 0)
      )[0];
      const firstTypeName =
        dataTypes.find((dt) => dt.data_type_id === Number(firstCol?.data_type_id))
          ?.data_type_name ?? "";
      if (!isGridHeadingDataTypeName(firstTypeName)) {
        messages.push(
          "When fixed rows are active, the first grid column must be VARCHAR or LONG_TEXT."
        );
      }
    }
  }

  return messages;
}
